import type { MediaKind } from "@velizhanin-dev/bot-scenario"
import { MapPin, Paperclip, Phone } from "lucide-react"
import { useState } from "react"
import type { TestEvent } from "@/entities/bot-builder"
import { MEDIA_KIND_LABELS } from "@/shared/config/media"
import { Button } from "@/shared/ui/button"
import { Input } from "@/shared/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover"
import { COMPOSER_ICON_CLASS } from "../lib/telegram-theme"

export const DEFAULT_TEST_PHONE = "+79001234567"
/** Красная площадь, чтобы не вводить координаты руками. */
export const DEFAULT_TEST_LOCATION = { latitude: 55.7539, longitude: 37.6208 }
const SIMULATED_MEDIA: MediaKind[] = [
	"photo",
	"video",
	"voice",
	"video_note",
	"document",
	"sticker",
]

/** Меню команд, как кнопка «/» в Telegram: /start с параметром и команды потоков. */
export function CommandsMenu({
	commands,
	disabled,
	onStart,
	onCommand,
}: {
	commands: { command: string; description?: string }[]
	disabled: boolean
	onStart: (payload?: string) => void
	onCommand: (command: string) => void
}) {
	const [open, setOpen] = useState(false)
	const [payload, setPayload] = useState("")
	const run = (action: () => void) => {
		setOpen(false)
		action()
	}
	const row = (command: string, description: string | undefined, onClick: () => void) => (
		<Button
			key={command}
			type="button"
			variant="ghost"
			className="h-auto w-full items-baseline justify-start gap-2 rounded-lg px-2.5 py-2 text-left"
			onClick={onClick}
		>
			<span className="font-mono text-sm text-sky-300">/{command}</span>
			{description ? (
				<span className="truncate text-xs text-muted-foreground">{description}</span>
			) : null}
		</Button>
	)
	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="ghost"
					size="icon"
					disabled={disabled}
					aria-label="Команды бота"
					title="Команды"
					className={COMPOSER_ICON_CLASS}
				>
					<span className="text-lg leading-none font-semibold">/</span>
				</Button>
			</PopoverTrigger>
			<PopoverContent side="top" align="start" className="w-72 rounded-xl p-1">
				{row("start", "начать сценарий заново", () => run(() => onStart()))}
				<form
					className="flex items-center gap-1 px-2 pt-0.5 pb-2"
					onSubmit={(event) => {
						event.preventDefault()
						if (payload) run(() => onStart(payload))
					}}
				>
					<Input
						aria-label="Параметр /start"
						placeholder="параметр ссылки, например promo"
						className="h-8 text-xs"
						maxLength={64}
						value={payload}
						onChange={(event) => setPayload(event.target.value.trim())}
					/>
					<Button
						type="submit"
						size="sm"
						variant="secondary"
						className="h-8 shrink-0"
						disabled={!payload}
					>
						Старт
					</Button>
				</form>
				{commands.length > 0 ? (
					<>
						<div className="my-1 h-px bg-border" />
						{commands.map((item) =>
							row(item.command, item.description, () => run(() => onCommand(item.command))),
						)}
					</>
				) : null}
			</PopoverContent>
		</Popover>
	)
}

/** Имитация того, что пользователь поделился контактом, геолокацией или прислал файл. */
export function AttachMenu({
	disabled,
	onSend,
}: {
	disabled: boolean
	onSend: (event: TestEvent) => void
}) {
	const [open, setOpen] = useState(false)
	const [phone, setPhone] = useState(DEFAULT_TEST_PHONE)
	const send = (event: TestEvent) => {
		setOpen(false)
		onSend(event)
	}
	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="ghost"
					size="icon"
					aria-label="Отправить контакт, геолокацию или файл"
					title="Вложение"
					disabled={disabled}
					className={COMPOSER_ICON_CLASS}
				>
					<Paperclip className="h-5 w-5" />
				</Button>
			</PopoverTrigger>
			<PopoverContent side="top" align="start" className="w-64 space-y-2 rounded-xl p-2">
				<form
					className="flex gap-1"
					onSubmit={(event) => {
						event.preventDefault()
						if (phone.trim()) send({ type: "contact", phone: phone.trim() })
					}}
				>
					<Input
						aria-label="Телефон для контакта"
						placeholder="+7 900 000-00-00"
						className="h-8 text-xs"
						value={phone}
						onChange={(event) => setPhone(event.target.value)}
					/>
					<Button type="submit" size="sm" variant="secondary" className="h-8 shrink-0">
						<Phone />
						Контакт
					</Button>
				</form>
				<Button
					type="button"
					size="sm"
					variant="secondary"
					className="w-full"
					onClick={() => send({ type: "location", ...DEFAULT_TEST_LOCATION })}
				>
					<MapPin />
					Геолокация
				</Button>
				<p className="text-[11px] text-muted-foreground">Файл (без загрузки, условный file_id):</p>
				<div className="grid grid-cols-2 gap-1">
					{SIMULATED_MEDIA.map((kind) => (
						<Button
							key={kind}
							type="button"
							size="sm"
							variant="outline"
							className="h-7 text-xs"
							onClick={() => send({ type: "media", mediaKind: kind, fileName: `test.${kind}` })}
						>
							{MEDIA_KIND_LABELS[kind]}
						</Button>
					))}
				</div>
			</PopoverContent>
		</Popover>
	)
}
